<!--
Copyright (c) 2020 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details.
-->

<template>
    <div class="fr-all-in-one-container">
        <idmUserDetails v-if="stages.idmUserDetails" :inline="true" :selfServiceDetails="selfServiceDetails" @toggle-keep-informed-visible="toggleKeepInformedVisibility" @uid-change="handleUidChange"></idmUserDetails>
        <p class="registration-consent-text">
        <b-form-checkbox plain v-if="keepInformedVisible" v-model="keepInformed"
                                 :value="true"
                                 :unchecked-value="false"
                                 @change="onChangeKeepInformed">
            {{$t("pages.selfservice.registration.regConsent.keepInformedConsent")}}
        </b-form-checkbox>
        </p>
        <p class="registration-consent-text">
        <b-form-checkbox plain v-if="enableReceiveMail" v-model="receiveMail"
                                 :value="true"
                                 :unchecked-value="false"
                                 :disabled="!keepInformed">
            {{$t("pages.selfservice.registration.regConsent.receiveMailText")}}
        </b-form-checkbox>
        </p>
        <p class="registration-consent-text-poa" v-if="isPoaClient">
        <b-form-checkbox plain v-model="smsConsent"
                                 :value="true"
                                 :unchecked-value="false">
            {{$t("pages.selfservice.registration.regConsent.smsText")}}
        </b-form-checkbox>
        </p>
        <p class="registration-consent-text" v-if="!isPoaClient">
            {{$t("pages.selfservice.registration.regConsent.text1")}}
            <a :href="termsUrl" target="_blank">{{$t(revibe ? "pages.selfservice.registration.revibe.osa" : "pages.selfservice.registration.regConsent.termsAnchorText")}}</a> <span v-if="!hidePrivacyConsent">{{$t("pages.selfservice.registration.regConsent.text2")}} </span>
            <a :href="policyUrl" target="_blank" v-if="!hidePrivacyConsent"> {{$t("pages.selfservice.registration.regConsent.privacyAnchorText")}}</a><span>{{$t("pages.selfservice.registration.regConsent.period")}}</span>
        </p>
        <kbaSecurityAnswerDefinitionStage v-if="stages.kbaSecurityAnswerDefinitionStage" :inline="true" :selfServiceDetails="selfServiceDetails"></KBASecurityAnswerDefinitionStage>
        <Captcha v-if="selfServiceDetails.requirements.recaptcha && !automationToken" :self-service-details="selfServiceDetails" ref="recaptcha"
            @captcha-loading="handleCaptchaLoading"
            @captcha-ready="handleCaptchaReady"></Captcha>

        <template v-if="stages.consent && selfServiceDetails.requirements && selfServiceDetails.requirements.consent">
            <b-modal ref="consentModal"
                     :title="$t('pages.selfservice.registration.consent.title')"
                     @hide="loading = false;">

                <Consent :inline="true" :selfServiceDetails="selfServiceDetails"></Consent>

                <b-form-checkbox plain v-model="consentCheck"
                                 :value="false"
                                 :unchecked-value="true">
                    {{$t("pages.selfservice.registration.consent.agreement")}}
                </b-form-checkbox>

                <div slot="modal-footer" class="w-100">
                    <b-btn :disabled="consentCheck" size="sm" class="float-right" variant="primary" @click="save">
                        {{$t("common.form.okay")}}
                    </b-btn>
                </div>
            </b-modal>
        </template>
        <!-- <fr-loading-button type="button" variant="primary" class="mt-2 mb-3 btn-lg btn-block"
                           :label="$t('common.form.cancel')"
                           :large="true"
                           @click="$router.go(-1)"></fr-loading-button> -->
        <fr-loading-button type="button" variant="primary"
                           ref="submitButton"
                           :label="$t('common.form.createAccount')"
                           :loading="loading"
                           :disabled="loading"
                           :large="true"
                           :useSpacing="false"
                           @click="submitForm"/>

        <!-- POA client specific disclaimer -->
        <p class="registration-disclaimer-text mt-3" v-if="isPoaClient">
            {{$t("pages.selfservice.registration.regConsent.poaDisclaimerPart1")}}
            <a :href="poaTermsUrl" target="_blank">{{$t("pages.selfservice.passwordReset.termsofuse")}}</a>{{$t("pages.selfservice.registration.regConsent.poaDisclaimerPart2")}}
            <a :href="poaPolicyUrl" target="_blank">{{$t("pages.selfservice.passwordReset.privacyPolicy")}}</a>{{$t("pages.selfservice.registration.regConsent.poaDisclaimerPart3")}}
            <a :href="poaContactUrl" target="_blank">{{$t("pages.selfservice.registration.regConsent.contactUsText")}}</a>{{$t("pages.selfservice.registration.regConsent.poaDisclaimerPart4")}}
        </p>

        <TermsAndConditions v-if="stages.termsAndConditions && selfServiceDetails.requirements && selfServiceDetails.requirements.terms" :inline="true" :selfServiceDetails="selfServiceDetails"></TermsAndConditions>
   </div>
</template>

<script>
import _ from 'lodash';
import Captcha from '../common/Captcha';
import Consent from './Consent';
import idmUserDetails from './UserDetails';
import kbaSecurityAnswerDefinitionStage from './KBASecurityAnswerDefinitionStage';
import LoadingButton from '@/components/utils/LoadingButton';
import TermsAndConditions from './TermsAndConditions';
import moment from 'moment';
import { CLIENT_ID, CLINICAL_EUROPE_CONSENT, COMMON_CONSENT } from '../../../utils/common';
import { getClientById, CLIENT, getMinimalAttributeClient, isUPCEnabled, resolveRegistrationClientId } from '../../../lib/getClientById';
import { getConsentPolicyByClientId } from '../../../lib/api/getConsentPolicy';
import { getParams } from '../../../lib/URLUtils';
if (typeof window.gaEvent === 'function') {
    window.gaEvent('Register', 'Register', 'Rendered', 'page rendered.');
}

/**
 * @description Selfservice stage that is used for combing multiple selfservice stages (User details, Captcha, KBA, Terms and Confitions and Consent)
 *
 **/
export default {
    name: 'All-In-One-Registration',
    props: {
        selfServiceDetails: { required: true }
    },
    components: {
        idmUserDetails,
        kbaSecurityAnswerDefinitionStage,
        TermsAndConditions,
        Consent,
        Captcha,
        'fr-loading-button': LoadingButton
    },
    data () {
        const clientId = resolveRegistrationClientId(this.getQueryStringValue('clientId'));
        let setUFCForCE = false;
        if (clientId) {
            if (clientId === CLIENT.HK_LONGMAN || clientId === CLIENT.CERT_PREP ||
                getClientById() === CLIENT.DAVIDE || getClientById() === CLIENT.PSC_CLIENT ||
                getClientById() === CLIENT.MHBBDC_CLIENT || getClientById() === CLIENT.MHBHPI_CLIENT ||
                getClientById()?.includes('clinical-europe')) {
                setUFCForCE = false;
            } else {
                setUFCForCE = true;
            }
        }
        this.getConsent(getClientById());
        let data = {
            stages: {},
            consentCheck: true,
            loading: false,
            keepInformedVisible: isUPCEnabled(clientId),
            keepInformed: setUFCForCE || false,
            clinicalEuropeClientId: clientId,
            hidePrivacyConsent: false,
            enableReceiveMail: false,
            receiveMail: setUFCForCE,
            hklongman: getClientById() === CLIENT.HK_LONGMAN,
            revibe: getClientById() === CLIENT.REVIBE,
            isClinicalEurope: getClientById()?.includes('clinical-europe') || false,
            isPoaClient: getClientById() === CLIENT.POA_CLIENT,
            smsConsent: false,
            consents: {
                ...COMMON_CONSENT
            },
            recaptchaToken: undefined,
            automationToken: getParams().get('automationToken'),
            uid: null
        };

        _.each(this.selfServiceDetails.requirements.stages, (name) => {
            data.stages[name] = true;
        });

        return data;
    },
    methods: {
        handleCaptchaLoading () {
            this.loading = true;
            console.log('Submit button disabled until grecaptcha is ready');
        },
        handleCaptchaReady () {
            this.loading = false;
            console.log('Submit button enabled because grecaptcha is ready');
        },
        async getConsent (clientId) {
            const consents = await getConsentPolicyByClientId(clientId, this.$i18n.locale);
            this.consents = consents;
            return consents;
        },
        submitForm () {
            if (this.selfServiceDetails.requirements.recaptcha && !this.automationToken) {
                this.$refs.recaptcha.executeScore('register')
                    .then((token) => {
                        this.recaptchaToken = token;
                        this.saveCheck();
                    }).catch((e) => {
                        console.log('Recaptcha error: ' + e);
                    });
            } else {
                this.saveCheck();
            }
        },

        getData () {
            let data = {};

            /* istanbul ignore next */
            _.each(this.$children, (child) => {
                let childData = {};

                if (child.getData) {
                    childData = child.getData();
                }

                data = _.merge(data, childData);
            });

            if (this.selfServiceDetails.requirements.consentEnabled) {
                data.consentGiven = 'true';
            }

            data.user.upc = getClientById() === CLIENT.KIBO ? false : this.keepInformed;
            // Add SMS consent value for POA client
            if (this.isPoaClient) {
                data.user.upc = this.smsConsent;
            }
            if (this.automationToken) {
                data.automationToken = this.automationToken;
                data.recaptchaToken = 'recaptcha bypassed';
            } else if (this.recaptchaToken) {
                data.recaptchaToken = this.recaptchaToken;
            }

            const clientId = resolveRegistrationClientId(this.getQueryStringValue('clientId'));
            if (clientId) {
                data.clientId = clientId;
            }
            return data;
        },

        saveCheck () {
            if (typeof window.gaEvent === 'function') {
                window.gaEvent('Register', 'Register', 'Submit', 'Submit button clicked.');
            }
            this.loading = true;

            if (this.selfServiceDetails.requirements.consentEnabled) {
                this.isValid().then(() => {
                    this.$refs.consentModal.show();
                });
            } else {
                /* istanbul ignore next */
                this.isValid().then(() => {
                    this.save();
                });
            }
        },

        parseDOB (value) {
            if (!value) {
                return null;
            }

            const parsedDate = moment(value, 'MM-YYYY', true);
            const endOfMonthDay = parsedDate.endOf('month');
            const [month, year] = value.split('-');
            const dobWithDay = `${month}-${endOfMonthDay.format('DD')}-${year}`;
            return dobWithDay;
        },
        save () {
            const dob = this.parseDOB(this.getData().user.dob);
            const data = dob ? {
                ...this.getData(),
                user: {
                    ...this.getData().user,
                    dob
                }
            } : this.getData();
            console.log('data to be sent to backend:', data);
            console.log('data to be sent to backend:', this.getData());

            const pearsonCreatedBy = getMinimalAttributeClient(data?.clientId);

            if (pearsonCreatedBy) {
                const formatedData = {
                    clientId: data.clientId || null,
                    recaptchaToken: data.recaptchaToken || null,
                    user: {
                        uid: data.user.mail || '',
                        userPassword: data.user.userPassword || '',
                        c: data.user.c || '',
                        mail: data.user.mail || '',
                        cn: data.user.mail || '',
                        sn: data.user.mail || '',
                        pearsonCreatedBy,
                        upc: this.isPoaClient ? this.smsConsent : (data.user.upc || false)
                    }
                };
                this.$emit('advanceStage', formatedData);
            } else {
                this.$emit('advanceStage', data);
            }

            if (typeof window.gaEvent === 'function') {
                window.gaEvent('Register', 'Register', 'Send', 'Sent request to backend.');
            }
            this.loading = false;
        },

        isValid () {
            let childChecks = [],
                validPromise = new Promise((resolve, reject) => {
                    if (this.$children) {
                        _.each(this.$children, (child, index) => {
                            /* istanbul ignore next */
                            if (this.$children[index].isValid) {
                                childChecks.push(this.$children[index].isValid());
                            }
                        });
                    }

                    Promise.all(childChecks).then((results) => {
                        let validCheck = true;

                        _.each(results, (check) => {
                            /* istanbul ignore next */
                            if (check === false) {
                                validCheck = false;
                            }
                        });

                        if (validCheck) {
                            resolve({ 'success': true });
                        } else {
                            this.displayNotification('error', this.$t('pages.selfservice.registration.pleaseComplete'));
                            this.loading = false;
                        }
                    });
                });

            return validPromise;
        },
        toggleKeepInformedVisibility (isVisible) {
            this.keepInformedVisible = isVisible;

            if (this.keepInformedVisible && this.getData().user.c === 'US') {
                this.keepInformed = true;
            }
        },
        getQueryStringValue (name) {
            let url = decodeURIComponent(window.location.href);
            name = name.replace(/[[]/, '\\["').replace(/[\]]/, '\\]');
            let regexS = '[\\?&]' + name + '=([^&#]*)';
            let regex = new RegExp(regexS);
            let results = regex.exec(url);
            return results == null ? null : results[1];
        },
        hideConsent () {
            this.hidePrivacyConsent = true;
        },
        enableReceiveMail (clientId) {
            if (clientId === CLIENT_ID.GERMAN) {
                return true;
            }
        },
        onChangeKeepInformed () {
            if (this.clinicalEuropeClientId === CLIENT_ID.GERMAN) {
                this.receiveMail = this.keepInformed;
            }
        },
        handleUidChange (newUid) {
            this.uid = newUid;
        }
    },
    computed: {
        termsUrl () {
            let clientId = this.clinicalEuropeClientId;
            const currentLocale = this.$i18n.locale;
            const localeMap = {
                'en': 'en_GB',
                'zh': 'zh_HK'
            };
            const mappedLocale = localeMap[currentLocale] || currentLocale;
            const HKLONGMAN_CONSENT = {
                TERMS: `https://www.pearson.com.hk/${mappedLocale}/universal-terms-of-service.html`
            };
            if (this.hklongman) {
                return HKLONGMAN_CONSENT.TERMS;
            } else if (clientId) {
                if (clientId.startsWith('clinical-europe-')) {
                    if (clientId === CLIENT_ID.FRENCH) {
                        return CLINICAL_EUROPE_CONSENT.FR;
                    } else {
                        return `https://${window.location.host}/static/consent/${clientId}-terms.html`;
                    }
                } else {
                    return this.consents.TERMS;
                }
            } else if (this.consents) {
                return this.consents.TERMS;
            } else {
                return COMMON_CONSENT.TERMS;
            }
        },
        policyUrl () {
            let clientId = this.clinicalEuropeClientId;
            const currentLocale = this.$i18n.locale;
            const localeMap = {
                'en': 'en_GB',
                'zh': 'zh_HK'
            };
            const mappedLocale = localeMap[currentLocale] || currentLocale;
            const HKLONGMAN_CONSENT = {
                PRIVACY_POLICY: `https://www.pearson.com.hk/${mappedLocale}/privacy-policy.html`
            };
            if (this.hklongman) {
                return HKLONGMAN_CONSENT.PRIVACY_POLICY;
            } else if (clientId) {
                if (clientId.startsWith('clinical-europe-')) {
                    if (clientId === CLIENT_ID.GERMAN || clientId === CLIENT_ID.SPANISH) {
                        return `https://${window.location.host}/static/consent/${clientId}-privacy.html`;
                    } else {
                        this.hideConsent();
                        return '';
                    }
                } else if (clientId === CLIENT.REVIBE) {
                    this.hideConsent();
                    return '';
                } else {
                    return this.consents.PRIVACY_POLICY;
                }
            } else if (this.consents) {
                return this.consents.PRIVACY_POLICY;
            } else {
                return COMMON_CONSENT.PRIVACY_POLICY;
            }
        },
        poaTermsUrl () {
            // Use consistent pattern like termsUrl, with potential for future client-specific overrides
            return this.isPoaClient ? 'https://www.connectionseducation.com/terms-of-use/' : this.termsUrl;
        },
        poaPolicyUrl () {
            // Use consistent pattern like policyUrl, with potential for future client-specific overrides
            return this.isPoaClient ? 'https://www.connectionseducation.com/privacy-policy/' : this.policyUrl;
        },
        poaContactUrl () {
            // Contact URL is always the same for POA client
            return 'https://www.pearsononlineacademy.com/resources/contact-us/';
        }
    }
};
</script>

<style scoped>
/* .btn-primary:hover {
    color: #fff;
    background-color: #5498b3 !important;
    border-color: #0d5b79 !important;
    border-top-color: rgb(0, 98, 204);
    border-right-color: rgb(0, 98, 204);
    border-bottom-color: rgb(0, 98, 204);
    border-left-color: rgb(0, 98, 204);
}
.btn-primary {
    color: #fff;
    background-color: #0f6c90 !important;
    border-color: #0d5b79 !important;
    border-top-color: rgb(0, 98, 204);
    border-right-color: rgb(0, 98, 204);
    border-bottom-color: rgb(0, 98, 204);
    border-left-color: rgb(0, 98, 204);
} */
.btn-primary:focus, .btn-primary.focus {
    -webkit-box-shadow: 0 0 0 0.2rem rgba(84, 152, 179, 0.5) !important;
    box-shadow: 0 0 0 0.2rem rgba(84, 152, 179, 0.5) !important;
}

.registration-disclaimer-text {
  color: #333;
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: "Open Sans";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 171.429% */
  a {
    color: #333;
    font-feature-settings: 'liga' off, 'clig' off;
    font-family: "Open Sans";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
  }
}

.registration-consent-text-poa {
  color: #333;
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: "Open Sans";
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 171.429% */
}

.registration-consent-text {
  color: #333;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 171.429% */
}

</style>
